import { gsd, optimizePeaks, joinBroadPeaks, setShape, } from 'ml-gsd';
import { xyExtract, xNoiseSanPlot, xAbsoluteMedian, } from 'ml-spectra-processing';
import { convertWidthToHz } from '../peaks/util/convertWidthToHz';
export function xyAutoPeaksPicking(data, options) {
    const { from, to, frequency, noiseLevel, thresholdFactor = 3, minMaxRatio = 0.05, broadRatio = 0.00025, useSanPlot = false, smoothY = true, optimize = false, factorLimits = 4, realTopDetection = true, shape = { kind: 'gaussian' }, optimization = { kind: 'lm' }, broadWidth = 0.25, sgOptions = {}, direction = 'positive', } = options;
    let pointsPerHz = 1 / frequency / (data.x[1] - data.x[0]);
    // we can consider a peak with of 0.5 Hz for the windowSize
    let ws = Math.max(Math.round(pointsPerHz / 2), 5);
    const { windowSize = ws - (ws % 2) + 1, polynomial = 3 } = sgOptions;
    // check falsy values including NaN
    if (!windowSize) {
        throw new XYAutoPeaksPickingWindowSizeError({
            windowSize,
            ws,
            pointsPerHz,
            frequency,
            x: data.x.slice(0, 2),
            sgOptions,
            autoWindowSizeFormula: 'ws - (ws % 2) + 1',
            pointsPerHzFormula: '1 / frequency / (x[1] - x[0])',
        });
    }
    if (data.x.length < windowSize)
        return [];
    if (from !== undefined && to !== undefined) {
        data = xyExtract(data, { zones: [{ from, to }] });
    }
    const cutOff = getCutOff(data.y, { noiseLevel, useSanPlot, thresholdFactor });
    let getPeakOptions = {
        shape,
        frequency,
        broadWidth,
        optimize,
        factorLimits,
        maxCriteria: true,
        sgOptions: { windowSize, polynomial },
        minMaxRatio,
        broadRatio,
        noiseLevel: cutOff.positive,
        smoothY,
        optimization,
        realTopDetection,
    };
    switch (direction) {
        case 'positive': {
            return getPeakList(data, getPeakOptions);
        }
        case 'negative': {
            return getNegativePeaks(data, cutOff, getPeakOptions);
        }
        case 'both': {
            return [
                ...getPeakList(data, getPeakOptions),
                ...getNegativePeaks(data, cutOff, getPeakOptions),
            ];
        }
        default: {
            const unHandled = direction;
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw Error(`Unknown distribution ${unHandled}`);
        }
    }
}
function getNegativePeaks(data, cutOff, getPeakOptions) {
    getPeakOptions.noiseLevel = cutOff.negative;
    getPeakOptions.maxCriteria = false;
    return getPeakList(data, getPeakOptions);
}
function getPeakList(data, options) {
    const { shape, frequency, broadWidth, optimize, maxCriteria, factorLimits, sgOptions, minMaxRatio, broadRatio, noiseLevel, smoothY, optimization, realTopDetection, } = options;
    const peaks = gsd(data, {
        sgOptions,
        maxCriteria,
        minMaxRatio,
        noiseLevel,
        smoothY,
        realTopDetection,
    });
    const peakList = setShape(peaks, { output: peaks, shape });
    const newPeaks = broadWidth
        ? joinBroadPeaks(peakList, {
            broadRatio,
            broadWidth,
            shape,
            optimization,
        }).map(toNMRPeak1DStructure)
        : peakList.map(toNMRPeak1DStructure);
    return convertWidthToHz(optimize
        ? optimizePeaks(data, newPeaks, {
            shape,
            factorLimits,
            optimization,
        }).map(toNMRPeak1DStructure)
        : newPeaks, { frequency });
}
function getCutOff(data, options) {
    const { noiseLevel, useSanPlot, thresholdFactor } = options;
    const formatResult = (noiseLevel) => typeof noiseLevel === 'number'
        ? { positive: noiseLevel, negative: -noiseLevel }
        : noiseLevel;
    if (noiseLevel) {
        return formatResult(noiseLevel);
    }
    else {
        return useSanPlot
            ? xNoiseSanPlot(data, { factorStd: thresholdFactor })
            : formatResult(xAbsoluteMedian(data) * thresholdFactor);
    }
}
function toNMRPeak1DStructure(peak) {
    const { id, shape, x, y, width } = peak;
    return {
        id,
        x,
        y,
        width,
        shape,
    };
}
/**
 * error for default windowSize computation
 * out in data, all data relative to the computation
 */
export class XYAutoPeaksPickingWindowSizeError extends Error {
    constructor(data) {
        super('windowSize must be an odd integer >= 5, check data property of this error for contextual information');
        this.data = data;
    }
}
//# sourceMappingURL=xyAutoPeaksPicking.js.map